import { useSoulMapDocuments } from '@/hooks/useSoulMapDocuments.ts';
import { TTSVoiceConfig } from '@/hooks/useTTS';
import { SoulMapDocumentControls } from '@/pages/chat-page/components/soul-map-document-controls.tsx';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@nextui-org/react';
// import { SoulMapDocumentControls } from './soul-map-document-controls';
// import React from 'react';

interface ChatHeaderProps {
  actionTitle: string;
  reStart: () => void;
  currentVoice: TTSVoiceConfig;
  setCurrentVoice: (voice: TTSVoiceConfig) => void;
  availableVoices: TTSVoiceConfig[];
  setIsSettingsOpen: (isOpen: boolean) => void;
  onBackButtonClick: () => void;
  practiceId?: string;
  practiceCreatedAt?: string;
  soulMapDocuments?: ReturnType<typeof useSoulMapDocuments>;
  treatExpiredDocumentAsPreparing?: boolean;
  regenerateExpiredDocument?: boolean;
  onReviewBirthDetails?: () => void;
  showRestartButton?: boolean;
}

const icons = {
  speaker: 'mdi:account-voice',
  speakerOff: 'mdi:account-voice-off',
  refresh: 'mdi:refresh',
  menu: 'mdi:dots-vertical',
  settings: 'mdi:cog',
};

export const ChatHeader = ({
  actionTitle,
  reStart,
  onBackButtonClick,
  practiceId,
  practiceCreatedAt,
  soulMapDocuments,
  treatExpiredDocumentAsPreparing = false,
  regenerateExpiredDocument = false,
  onReviewBirthDetails,
  showRestartButton = true,
}: ChatHeaderProps) => {
  const handleBack = () => {
    onBackButtonClick();
  };

  // Commented out - kept for future use
  // console.log(availableVoices, 'availableVoices');

  // const dropdownItems: React.ReactElement[] = [
  //   // <DropdownItem
  //   //   key="settings"
  //   //   startContent={
  //   //     <Icon
  //   //       icon={icons.settings}
  //   //       className="text-neutral-500"
  //   //       style={{ fontSize: '20px' }}
  //   //     />
  //   //   }
  //   //   onClick={() => setIsSettingsOpen(true)}
  //   // >
  //   //   Settings
  //   // </DropdownItem>,
  // ];

  // if (selectedPrompt?.enable_audio) {
  //   dropdownItems.push(
  //     <DropdownItem key="send-button">
  //       <div className="flex items-center justify-between gap-2">
  //         <span>Show Send Button in Voice Mode</span>
  //         <Switch
  //           size="sm"
  //           isSelected={showSendButton}
  //           onValueChange={setShowSendButton}
  //         />
  //       </div>
  //     </DropdownItem>
  //   );

  //   dropdownItems.push(
  //     <DropdownItem key="realtime-mode">
  //       <div className="flex items-center justify-between gap-2">
  //         <span>Realtime Mode</span>
  //         <Switch
  //           size="sm"
  //           isSelected={realtimeModeEnabled}
  //           onValueChange={setRealtimeModeEnabled}
  //         />
  //       </div>
  //     </DropdownItem>
  //   );
  // }

  // if (enableTTS && availableVoices.length > 0) {
  //   dropdownItems.push(
  //     <DropdownItem key="voice-selector" className="p-0">
  //       <div className="flex flex-col gap-2 p-2">
  //         <span className="text-small text-default-600">
  //           Voice - Speech to text
  //         </span>
  //         <Select
  //           size="sm"
  //           isDisabled
  //           selectedKeys={[currentVoice?.voice]}
  //           onSelectionChange={(keys) => {
  //             const selectedKey = Array.from(keys)[0] as string;
  //             const voice = availableVoices.find(
  //               (v) => v.voice === selectedKey
  //             );
  //             if (voice) setCurrentVoice(voice);
  //           }}
  //           className="w-full"
  //           classNames={{
  //             trigger: 'min-h-8 h-8',
  //             value: 'text-small',
  //           }}
  //         >
  //           {availableVoices.map((voice) => (
  //             <SelectItem key={voice.voice} value={voice.voice}>
  //               {voice.voice}
  //             </SelectItem>
  //           ))}
  //         </Select>
  //       </div>
  //     </DropdownItem>
  //   );
  // }

  // The subtitle shows the chat's own date, the same one Recent Chats lists.
  // Until the practice loads the date is left out rather than guessed.
  const formatDate = () => {
    if (!practiceCreatedAt) return null;
    const date = new Date(practiceCreatedAt);
    if (Number.isNaN(date.getTime())) return null;
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${month}`;
  };
  const subtitleDate = formatDate();

  return (
    <div className="border border-gray-200 rounded-xl p-3 md:p-4 w-full flex flex-wrap justify-between items-center gap-x-3 bg-white dark:bg-default-100">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Button
          isIconOnly
          variant="light"
          size="sm"
          onClick={handleBack}
          aria-label="Go Back"
          className="flex-shrink-0"
        >
          <Icon
            icon="mdi:arrow-left"
            className="text-neutral-500 dark:text-neutral-400"
            style={{ fontSize: '20px' }}
          />
        </Button>
        <div className="flex flex-col min-w-0">
          <h2 className="text-base md:text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate">
            {actionTitle || 'Lift Coach'}
          </h2>
          <span className="text-xs text-gray-400 truncate">
            {subtitleDate ? `${subtitleDate} - ` : ''}
            {actionTitle || 'Lift Coach'}
          </span>
        </div>
      </div>
      {soulMapDocuments && practiceId ? (
        <SoulMapDocumentControls
          practiceId={practiceId}
          statusQuery={soulMapDocuments}
          treatExpiredAsPreparing={treatExpiredDocumentAsPreparing}
          regenerateExpired={regenerateExpiredDocument}
          onReviewBirthDetails={onReviewBirthDetails}
        />
      ) : null}

      <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
        {/* Voice output toggle - commented out for cleaner design */}
        {/* {selectedPrompt?.enable_audio && (
          <Tooltip
            content={enableTTS ? 'Disable Voice output' : 'Enable Voice output'}
          >
            <Button
              isIconOnly
              variant="light"
              onClick={() => setEnableTTS(!enableTTS)}
              aria-label={enableTTS ? 'Disable Voice' : 'Enable Voice'}
            >
              <Icon
                icon={enableTTS ? icons.speaker : icons.speakerOff}
                className="text-neutral-500 dark:text-neutral-400"
                style={{ fontSize: '20px' }}
              />
            </Button>
          </Tooltip>
        )} */}

        {showRestartButton && (
          <Tooltip content="Restart Chat">
            <Button
              isIconOnly
              variant="light"
              onClick={reStart}
              aria-label="Restart Chat"
            >
              <Icon
                icon={icons.refresh}
                className="text-neutral-500 dark:text-neutral-400"
                style={{ fontSize: '20px' }}
              />
            </Button>
          </Tooltip>
        )}

        {/* More options dropdown - commented out for cleaner design */}
        {/* <Dropdown>
          <DropdownTrigger>
            <Button isIconOnly variant="light" aria-label="More Options">
              <Icon
                icon={icons.menu}
                className="text-neutral-500 dark:text-neutral-400"
                style={{ fontSize: '20px' }}
              />
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Chat Options">
            {dropdownItems.filter(Boolean)}
          </DropdownMenu>
        </Dropdown> */}
      </div>
    </div>
  );
};
